"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { TestIdEnum } from "../../../tests/types";
import type { Option } from "../../types/selects";
interface SingleSelectFilterProps<Clearable extends boolean | undefined> {
  options: Option[];
  selected?: string;
  onChange: Clearable extends true ? (value: string | undefined) => void : (value: string) => void;
  clearable?: Clearable;
  placeholder?: string;
  allowSearching?: boolean;
  id: string;
}
export const SingleSelectFilter = <Clearable extends boolean | undefined,>({
  options,
  selected,
  onChange,
  placeholder = "Wybierz...",
  allowSearching = true,
  clearable,
  id
}: SingleSelectFilterProps<Clearable>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectedOptionLabel = options.find(opt_0 => opt_0.value === selected)?.label || placeholder;
  useEffect(() => {
    setIsMounted(true);
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleSelect = (value: string) => {
    onChange(value);
    setIsOpen(false);
    setSearchQuery("");
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clearable) {
      (onChange as SingleSelectFilterProps<true>["onChange"])(undefined);
      setIsOpen(false);
      setSearchQuery("");
    }
  };
  const getDropdownPosition = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    };
  };
  return <div className="relative">
      <button ref={triggerRef} type="button" onClick={() => setIsOpen(!isOpen)} data-testid={`${TestIdEnum.FiltersSingleButtonPrefix}-${id}`} className="flex items-center justify-between w-full p-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:border-brand-500 outline-none dark:text-white placeholder-slate-400 cursor-pointer">
        <span className="truncate">{selectedOptionLabel}</span>
        <span className="flex items-center gap-1">
          {clearable && selected && <FiX className="w-4 h-4 text-slate-400 hover:text-red-500" onClick={handleClear} data-testid={`${TestIdEnum.FiltersSingleOptionPrefix}-clear`} />}
          <FiChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </span>
      </button>

      {isOpen && isMounted && createPortal(<div ref={dropdownRef} style={getDropdownPosition()} className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg animate-fade-in">
            {allowSearching && <div className="relative p-2 border-b border-slate-200 dark:border-slate-700">
                <input type="text" placeholder="Szukaj..." value={searchQuery} onChange={e_0 => setSearchQuery(e_0.target.value)} className="w-full p-2 md:p-1 pl-7 text-xs bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none dark:text-white placeholder-slate-400 focus:z-10" autoFocus />
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              </div>}
            <div className="max-h-48 overflow-y-auto scrollbar-thin">
              {filteredOptions.length > 0 ? filteredOptions.map(option => <button key={option.value} onClick={() => handleSelect(option.value)} data-testid={`${TestIdEnum.FiltersSingleOptionPrefix}-${option.value}`} className={`block w-full text-left px-3 py-3 md:py-2 text-xs transition-colors ${selected === option.value ? "bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 font-semibold" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"}`}>
                    {option.label}
                  </button>) : <div className="px-3 py-2 text-xs text-slate-400 text-center">Brak wyników</div>}
            </div>
          </div>, document.body)}
    </div>;
};